import {
  Box,
  Divider,
  List,
  ListItem,
  ListItemText,
  Paper,
  Typography
} from '@mui/material'
import Button from '@mui/material/Button'
import { Notification } from '@/modules/utilities/notification/types/notification.ts'
import { useNavigate } from 'react-router-dom'
import { frontPaths } from '@/config/frontPaths.ts'
import useActions from '@/modules/utilities/notification/components/listNotifications/useActions.ts'
import { mapBackToFrontNotifications } from '@/modules/utilities/notification/utils/mapBackToFrontNotifications.ts'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import 'dayjs/locale/es'

dayjs.extend(relativeTime)
dayjs.extend(customParseFormat)
dayjs.locale('es')

const formatTimeAgo = (createdAt: string): string => {
  const date = dayjs(createdAt)
  if (!date.isValid()) {
    return 'Fecha inválida'
  }
  return date.fromNow()
}

type Props = {
  notifications: Notification[]
  isFetching: boolean
  isMarking: boolean
  handleMarkAll: () => void
}

const NotificationPaper = ({
  notifications,
  handleMarkAll,
  isMarking
}: Props) => {
  const navigate = useNavigate()

  const { handlerSelect } = useActions()

  const handleMarkAsRead = (item: Notification) => {
    handlerSelect(item.id)
    const url = mapBackToFrontNotifications(item.requestReference)
    if (url) navigate(url)
  }

  return (
    <>
      <Paper
        sx={{
          width: 360,
          maxWidth: '100%',
          mt: 1.5,
          boxShadow: 'rgb(0 0 0 / 24%) 0px 3px 8px'
        }}
      >
        <Box sx={{ p: 2 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <Typography variant="h6" component="div">
              Notificaciones
            </Typography>

            <Button
              size="small"
              onClick={(e) => {
                e.preventDefault()
                handleMarkAll()
              }}
              loading={isMarking}
            >
              Marcar como leídas
            </Button>
          </Box>
        </Box>

        <Divider />

        <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
          <List disablePadding>
            {notifications.length > 0 ? (
              notifications.map((notification) => (
                <ListItem
                  key={notification.id}
                  sx={{
                    bgcolor:
                      notification.requestStatus !== 'LEÍDA'
                        ? 'action.hover'
                        : 'background.paper',
                    '&:hover': { bgcolor: 'action.selected' }
                  }}
                  onClick={() => handleMarkAsRead(notification)}
                >
                  <ListItemText
                    primary={
                      notification.message || 'Error: Mensaje no disponible'
                    }
                    primaryTypographyProps={{
                      fontSize: 14,
                      fontWeight:
                        notification.requestStatus !== 'LEÍDA'
                          ? 'bold'
                          : 'regular'
                    }}
                    secondary={formatTimeAgo(notification.createdAt)}
                    secondaryTypographyProps={{
                      fontSize: 12,
                      color: 'text.secondary'
                    }}
                  />
                </ListItem>
              ))
            ) : (
              <ListItem>
                <ListItemText primary="No tienes notificaciones" />
              </ListItem>
            )}
          </List>
        </Box>

        <Divider />

        {/* Pie de página */}
        <Box sx={{ p: 1, textAlign: 'center' }}>
          <Button fullWidth onClick={() => navigate(frontPaths.notifications)}>
            Ver todas las notificaciones
          </Button>
        </Box>
      </Paper>
    </>
  )
}

export default NotificationPaper
